import {type CSSProperties, type HTMLAttributes, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {FormSubmissionProvider, useFormSubmission} from '../contexts/form-submission-context';
import {ConversationalForm, FormHeader, FormNavigation, FormProgress, FormSubmissionCanvas, FormSuccessMessage, QuizResultsDisplay,} from '../components/form-submission';
import {Alert, AlertDescription, AlertTitle, Button, cn, useLanguage, useTheme, LoadingSkeleton} from '@shondalai/ui-commons';
import {AlertCircle, MessageSquare} from 'lucide-react';
import {useGoogleAnalyticsIntegration} from '../hooks/use_google_analytics_integration';
import {TranslationProvider, FormLanguageSwitcher} from '../components/translations';
import {EasyFormsToaster} from '../components/easyforms-toaster';
import {
  type EasyFormsColorScheme,
  FormColorSchemeProvider,
} from '../contexts/form-color-scheme-context';

const COLOR_MODE_THEME_VARIABLES: Record<string, string> = {
  background: 'hsl(var(--background))',
  surface: 'hsl(var(--card))',
  border: 'hsl(var(--border))',
  text: 'hsl(var(--foreground))',
  textSecondary: 'hsl(var(--muted-foreground))',
};

function FormColorSchemeSurface({
  scheme,
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & {scheme: EasyFormsColorScheme}) {
  return (
    <FormColorSchemeProvider scheme={scheme}>
      <div
        {...props}
        className={cn(className, scheme)}
        data-easyforms-color-scheme={scheme}
      >
        {children}
        <EasyFormsToaster position="top-right" />
      </div>
    </FormColorSchemeProvider>
  );
}

function FormSubmissionContent() {
  const { t } = useLanguage();
  const {actualTheme} = useTheme();

  // Get formId from Joomla options (when used as standalone view)
  const joomlaOptions = (window as any).Joomla?.getOptions?.('com_easyforms') || {};

  // Get formId
  const formId = joomlaOptions?.formId || null;

  const {
    form,
    isLoading,
    loadError,
    submissionComplete,
    submissionResponse,
    loadForm,
  } = useFormSubmission();

  // F6: the form is offered as a chat only when the server says so. The public
  // payload resolves the per-form flag against the site's AI settings, so a
  // `true` here already means "on, connected and allowed for this visitor".
  // Leaving the chat is one click and never comes back on its own.
  const conversationalOffered = form?.settings?.ai?.conversational === true;
  const [conversationalActive, setConversationalActive] = useState(true);

  const requestedColorScheme = form?.settings?.theme;
  const formColorScheme: EasyFormsColorScheme = requestedColorScheme === 'light' || requestedColorScheme === 'dark'
    ? requestedColorScheme
    : actualTheme;
  const loadedFormId = form?.id;

  // A form's explicit Light/Dark choice is authoritative for the public mount,
  // including Tailwind dark variants and portaled controls. Auto tracks the
  // host/OS value exposed by the shared ThemeProvider.
  useLayoutEffect(() => {
    if (!loadedFormId) return;

    const root = document.getElementById('easyforms-app');
    if (!root) return;

    const enforceFormScheme = () => {
      if (
        root.classList.contains(formColorScheme) &&
        !root.classList.contains(formColorScheme === 'dark' ? 'light' : 'dark') &&
        root.dataset.colorScheme === formColorScheme
      ) {
        return;
      }

      root.classList.remove('light', 'dark');
      root.classList.add(formColorScheme);
      root.dataset.colorScheme = formColorScheme;
      root.style.colorScheme = formColorScheme;
    };

    enforceFormScheme();

    const explicitFormScheme = requestedColorScheme === 'light' || requestedColorScheme === 'dark';
    const observer = explicitFormScheme
      ? new MutationObserver(enforceFormScheme)
      : null;
    observer?.observe(root, {
      attributes: true,
      attributeFilter: ['class', 'data-color-scheme'],
    });

    return () => {
      observer?.disconnect();
      root.classList.remove('light', 'dark');
      root.classList.add(actualTheme);
      root.dataset.colorScheme = actualTheme;
      root.style.colorScheme = actualTheme;
    };
  }, [actualTheme, formColorScheme, loadedFormId, requestedColorScheme]);

  // Google Analytics integration (no params needed - uses form from context)
  const { trackFormSubmission } = useGoogleAnalyticsIntegration();

  // Load form
  useEffect(() => {
    if (formId) {
      loadForm(formId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  // Load base theme CSS when form has theme
  useEffect(() => {
    if (!form?.theme) return;

    // Load base theme CSS first (if not already loaded)
    const baseStyleId = 'theme-base';
    let baseStyleEl = document.getElementById(baseStyleId) as HTMLLinkElement;

    if (!baseStyleEl) {
      baseStyleEl = document.createElement('link');
      baseStyleEl.id = baseStyleId;
      baseStyleEl.rel = 'stylesheet';
      const mediaBase = String(joomlaOptions?.mediaBase || '/media/com_easyforms').replace(/\/$/, '');
      const version = joomlaOptions?.version ? `?v=${encodeURIComponent(String(joomlaOptions.version))}` : '';
      baseStyleEl.href = `${mediaBase}/themes/base.css${version}`;
      document.head.appendChild(baseStyleEl);
    }
  }, [form?.theme]);

  // Compute theme CSS variables as inline styles
  const themeStyles = useMemo(() => {
    if (!form?.theme?.theme) return undefined;

    const { theme, customizations } = form.theme;
    // Color Theme (Light / Dark / Auto) controls the core surfaces while the
    // selected form theme keeps its accent, typography, spacing, and layout.
    // Explicit per-form customizations remain the final override.
    const variables = {
      ...theme.variables,
      ...COLOR_MODE_THEME_VARIABLES,
      ...customizations,
    };

    // Convert theme variables to CSS custom properties
    const cssVars: Record<string, string> = {};
    Object.entries(variables).forEach(([key, value]) => {
      cssVars[`--ef-${key}`] = String(value);
    });

    return cssVars as CSSProperties;
  }, [form?.theme]);

  // Track form submission
  useEffect(() => {
    if (submissionComplete && submissionResponse) {
      trackFormSubmission(submissionResponse.submissionId);
    }
  }, [submissionComplete, submissionResponse, trackFormSubmission]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 py-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <LoadingSkeleton variant="list" lines={8} showAvatar={false} className="mb-4" />
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    // Check if this is a token-related error
    const isTokenError = loadError.toLowerCase().includes('token') ||
                         loadError.toLowerCase().includes('access');

    if (isTokenError) {
      // Beautiful token error page (Swiss spa design)
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
          <div className="max-w-lg w-full">
            <div className="bg-card border border-border/50 rounded-lg shadow-sm overflow-hidden">
              {/* Header */}
              <div className="bg-muted/30 border-b border-border/50 px-8 py-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <h1 className="text-2xl font-light text-foreground tracking-tight">
                  {t('COM_EASYFORMS_ACCESS_DENIED', 'Access Denied')}
                </h1>
              </div>

              {/* Content */}
              <div className="px-8 py-8 space-y-6">
                <div className="space-y-3">
                  <h2 className="text-base font-medium text-foreground">
                    {t('COM_EASYFORMS_INVALID_TOKEN_TITLE', 'Invalid or Expired Access Link')}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t('COM_EASYFORMS_INVALID_TOKEN_MESSAGE',
                      'The access link you used is either invalid, has expired, or has already been used. This form requires a valid access token to continue.')}
                  </p>
                </div>

                {/* Divider */}
                <div className="border-t border-border/50" />

                {/* Possible reasons */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-foreground">
                    {t('COM_EASYFORMS_POSSIBLE_REASONS', 'Possible Reasons')}
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/40 mt-0.5">•</span>
                      <span>{t('COM_EASYFORMS_TOKEN_REASON_EXPIRED', 'The link has expired')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/40 mt-0.5">•</span>
                      <span>{t('COM_EASYFORMS_TOKEN_REASON_USED', 'The link has already been used (single-use token)')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/40 mt-0.5">•</span>
                      <span>{t('COM_EASYFORMS_TOKEN_REASON_INVALID', 'The link is incomplete or incorrect')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/40 mt-0.5">•</span>
                      <span>{t('COM_EASYFORMS_TOKEN_REASON_REVOKED', 'The access has been revoked')}</span>
                    </li>
                  </ul>
                </div>

                {/* Divider */}
                <div className="border-t border-border/50" />

                {/* Next steps */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-foreground">
                    {t('COM_EASYFORMS_WHAT_TO_DO', 'What You Can Do')}
                  </h3>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <p>
                      {t('COM_EASYFORMS_TOKEN_CONTACT_SENDER',
                        'Please contact the person who sent you this link to request a new access link.')}
                    </p>
                    <p className="text-xs">
                      {t('COM_EASYFORMS_TOKEN_CHECK_EMAIL',
                        'Make sure you\'re using the complete link from your invitation email and haven\'t modified it.')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-muted/20 border-t border-border/50 px-8 py-4 text-center">
                <button
                  onClick={() => window.history.back()}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors font-light"
                >
                  {t('COM_EASYFORMS_GO_BACK', '← Go Back')}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Generic error for non-token errors
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
        <div className="max-w-md w-full">
          <Alert variant="destructive" className="border-destructive/20 bg-destructive/5">
            <AlertCircle className="h-4 w-4" strokeWidth={1.5} />
            <AlertTitle className="text-base font-medium">{t('COM_EASYFORMS_FORM_LOAD_ERROR_TITLE', 'Unable to Load Form')}</AlertTitle>
            <AlertDescription className="mt-1.5 text-sm">
              {loadError}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // No form found
  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
        <div className="max-w-md w-full">
          <Alert className="border-border/40 bg-muted/30">
            <AlertCircle className="h-4 w-4" strokeWidth={1.5} />
            <AlertTitle className="text-base font-medium">{t('COM_EASYFORMS_FORM_NOT_FOUND_TITLE', 'Form Not Found')}</AlertTitle>
            <AlertDescription className="mt-1.5 text-sm">
              {t('COM_EASYFORMS_FORM_NOT_FOUND_MESSAGE', "The form you're looking for doesn't exist or is no longer available.")}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Check if user has already submitted (and multiple submissions not allowed)
  if (form.alreadySubmitted) {
    return (
      <FormColorSchemeSurface scheme={formColorScheme} className="min-h-screen flex bg-gradient-to-br from-background via-background to-muted/20 p-4">
        <div className="w-full">
          <Alert className="border-[hsl(var(--warning)/0.32)] bg-[hsl(var(--warning)/0.10)]">
            <AlertCircle className="h-4 w-4 text-[hsl(var(--warning-text))]" strokeWidth={1.5} />
            <AlertTitle className="text-base font-medium text-[hsl(var(--warning-text))]">
              {t('COM_EASYFORMS_FORM_ALREADY_SUBMITTED_TITLE', 'Already Submitted')}
            </AlertTitle>
            <AlertDescription className="mt-2 text-sm text-[hsl(var(--warning-text))]">
              <div className="space-y-3">
                <p className="font-medium">
                  {form.message || t('COM_EASYFORMS_FORM_ALREADY_SUBMITTED_MESSAGE', 'You have already submitted this form and multiple submissions are not allowed.')}
                </p>
                {form.title && (
                  <div className="pt-3 border-t border-[hsl(var(--warning)/0.28)]">
                    <p className="text-xs text-[hsl(var(--warning-text))]">
                      <strong>{t('COM_EASYFORMS_FORM_LABEL', 'Form')}:</strong> {form.title}
                    </p>
                    {form.description && (
                      <p className="text-xs text-[hsl(var(--warning-text))] mt-1">
                        {form.description}
                      </p>
                    )}
                  </div>
                )}
                <p className="text-xs text-[hsl(var(--warning-text))] pt-2">
                  {t('COM_EASYFORMS_FORM_ERROR_CONTACT_ADMIN', 'If you believe this is an error, please contact the form administrator.')}
                </p>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </FormColorSchemeSurface>
    );
  }

  // Submission complete - show success message
  if (submissionComplete) {
    return (
      <FormColorSchemeSurface scheme={formColorScheme} className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 py-8 px-4">
        <div className="container mx-auto">
          <FormSuccessMessage />

          {/* Show quiz results if applicable */}
          {submissionResponse?.showResults && form.formType === 'quiz' && (
            <div className="mt-6">
              <QuizResultsDisplay />
            </div>
          )}
        </div>
      </FormColorSchemeSurface>
    );
  }

  // Main form rendering
  return (
    <FormColorSchemeSurface
      scheme={formColorScheme}
      className={cn(
        "easyforms-submission-form min-h-screen",
        !form?.theme && "bg-gradient-to-br from-background via-background to-muted/20"
      )}
      style={themeStyles}
    >
      <div className={cn(
        "container mx-auto py-4 px-4 md:py-5 lg:py-6"
      )}>
        {/* Language Switcher - Show if form has multilingual enabled */}
        {form?.settings?.enableMultilingual && (
          <div className="flex justify-end mb-4">
            <FormLanguageSwitcher variant="minimal" />
          </div>
        )}

        {/* Form Card */}
        <div
          className={cn(
            "easyforms-form-container",
            // Default classes (can be overridden)
            !form.settings.containerCssClass && !form?.theme && [
              "bg-card/95 backdrop-blur-sm rounded-lg border border-border/40",
              "shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_16px_rgba(0,0,0,0.06)]",
              "p-5 md:p-8 lg:p-10",
              "transition-shadow duration-300"
            ],
            // Custom classes override defaults
            form.settings.containerCssClass
          )}
        >
          {conversationalOffered && conversationalActive ? (
            /* Conversational mode (F6): one question at a time, in a chat */
            <ConversationalForm onSwitchToStandard={() => setConversationalActive(false)} />
          ) : (
            <>
              {conversationalOffered && (
                <div className="mb-4 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => setConversationalActive(true)}
                  >
                    <MessageSquare className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                    {t('COM_EASYFORMS_AI_CONVERSATION_SWITCH_BACK', 'Fill this in as a chat')}
                  </Button>
                </div>
              )}

              {/* Form Header */}
              <FormHeader />

              {/* Progress Indicator (for multi-page forms) */}
              <FormProgress />

              {/* Form Fields Canvas */}
              <FormSubmissionCanvas />

              {/* Navigation Buttons */}
              <FormNavigation />
            </>
          )}
        </div>

        {/* Footer Text */}
        {joomlaOptions.showCredits && (
          <div className="text-center mt-5">
            <p className="text-xs text-muted-foreground/60 font-light">
              {t('COM_EASYFORMS_FORM_POWERED_BY', 'Powered by EasyForms')}
            </p>
          </div>
        )}
      </div>
    </FormColorSchemeSurface>
  );
}

export default function FormSubmission() {
  // Get formId from Joomla options
  const joomlaOptions = (window as any).Joomla?.getOptions?.('com_easyforms') || {};
  const formId = joomlaOptions?.formId || null;
  const defaultLanguage = joomlaOptions?.defaultLanguage || 'en-GB';

  return (
    <FormSubmissionProvider>
      {formId ? (
        <TranslationProvider
          formId={formId}
          defaultLanguage={defaultLanguage}
          detectionMethod="joomla"
          enableLanguageSwitcher={true}
        >
          <FormSubmissionContent />
        </TranslationProvider>
      ) : (
        <FormSubmissionContent />
      )}
    </FormSubmissionProvider>
  );
}
